import { adjectives, animals, colors, uniqueNamesGenerator } from 'unique-names-generator'

import CURRENCIES from './constants/currencies'
import {
  BASE_GAME_MINUTES_PER_TICK,
  SKILL_LEVEL_CAP,
  TICK_INTERVAL_MILLISECONDS,
  BASE_ENERGY_REGENERATION_PER_HOUR,
  WORK_MINUTES_PER_ENERGY,
  SURF_MINUTES_PER_ENERGY
} from './constants/game'
import LOCATIONS from './constants/locations'
import { getAvailableChapters, QUESTS } from './constants/quests'
import SKILLS from './constants/skills'
import SKILL_TREE from './constants/skillTree'
import { UPGRADES } from './constants/upgrades'
import {
  AutomationEnum,
  Automations,
  CompetitionEnum,
  CompetitionStats,
  Currencies,
  Currency,
  CurrencyState,
  CurrencyStates,
  Equipment,
  GamePlayData,
  GameSpeedTier,
  JobEnum,
  LocationEnum,
  LocationStats,
  QuestEnum,
  SingleCompetitionStatEnum,
  SkillEnum,
  SkillState,
  SkillStates,
  SkillTreeLevel,
  SkillUnlockRequirements,
  Stats,
  SurfSessionData,
  UnlockRule,
  Upgrade,
  Upgrades,
  UpgradeState,
  UpgradeStates,
  CareerPath
} from './types'
import {
  BASE_OFFLINE_STOKE_GAIN_PER_HOUR,
  BASE_STOKE_CAP,
  INITIAL_UNLOCKED_SPEEDS,
  STOKE_GAME_SPEED_COSTS
} from './constants/stoke'
import { JOBS, CAREERS } from './constants/jobs'
import { supabase } from '@/utils/supabase'
const SAVE_KEY = 'save'

let _isDemo = true // default value

export function setIsDemo(value: boolean) {
  _isDemo = value
}

// Fallback for non-component code
export function isDemo(): boolean {
  return _isDemo
}

export function saveGameDataLocal(gameData: GamePlayData) {
  localStorage.setItem(SAVE_KEY, JSON.stringify(gameData))
}

export function deleteGameData() {
  localStorage.removeItem(SAVE_KEY)
}

export function loadGameDataLocal(): GamePlayData {
  const save = localStorage.getItem(SAVE_KEY)
  if (save) {
    const gameData = stateMigrations(JSON.parse(save))
    return gameData
  }
  return createEmptyGameData()
}

export function exportGameData() {
  const gameData = loadGameDataLocal()
  const timestamp = new Date().toISOString()
  const questName = gameData.activeMainQuest || 'goat_surfer'
  const filename = `${questName}_${timestamp}.json`
  const blob = new Blob([JSON.stringify(gameData)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  return { url, filename }
}

export function createEmptyUpgradeStates(): UpgradeStates {
  const emptyUpgradeStates: any = {}
  for (const key in UPGRADES) {
    emptyUpgradeStates[key] = { isPurchased: false } as UpgradeState
  }
  return emptyUpgradeStates as UpgradeStates
}

export function createEmptyEquipment(): Equipment {
  return {
    surfboard: undefined,
    housing: undefined,
    other: []
  }
}

export function createEmptyCurrencies(): CurrencyStates {
  const emptyCurrencies: any = {}
  for (const key in CURRENCIES) {
    emptyCurrencies[key] = {
      amount: 0
    } as CurrencyState
  }
  emptyCurrencies['energy'].amount = 20
  emptyCurrencies['energy'].max = 20
  emptyCurrencies['energy'].totalMultiplier = 1
  emptyCurrencies['energy'].appliedMultipliers = [
    { category: 'permanent', sourceCategory: 'currency', sourceName: 'energy', multiplier: 1 }
  ]
  emptyCurrencies['money'].amount = 80
  emptyCurrencies['crit_probability'].amount = 0.01
  emptyCurrencies['crit_probability'].totalMultiplier = 1
  emptyCurrencies['crit_probability'].appliedMultipliers = [
    { category: 'permanent', sourceCategory: 'currency', sourceName: 'crit_probability', multiplier: 1 }
  ]
  emptyCurrencies['crit_multiplier'].amount = 2
  emptyCurrencies['crit_multiplier'].totalMultiplier = 1
  emptyCurrencies['crit_multiplier'].appliedMultipliers = [
    { category: 'permanent', sourceCategory: 'currency', sourceName: 'crit_multiplier', multiplier: 1 }
  ]
  emptyCurrencies['skill'].amount = Object.values(createEmptySkills()).reduce((sum, skill) => sum + skill.points, 0)
  emptyCurrencies['skill'].totalMultiplier = 1
  emptyCurrencies['skill'].appliedMultipliers = [
    { category: 'permanent', sourceCategory: 'currency', sourceName: 'skill', multiplier: 1 }
  ]
  emptyCurrencies['stoke'] = createEmptyStokeState()
  return emptyCurrencies as CurrencyStates
}

export function createEmptySkills(): SkillStates {
  const emptySkills: any = {}
  const autoUnlockSkills = []
  for (const key in SKILLS) {
    emptySkills[key] = { level: 0, points: 0 } as SkillState
  }
  /* for (const skill of autoUnlockSkills) {
    emptySkills[skill] = { level: 1, points: 100 } as SkillState
  } */
  return emptySkills as SkillStates
}

export function createEmptyStats(): Stats {
  return {
    wavesTotal: 0,
    ridingWaveSecondsTotal: 0,
    waitingForWaveMinutesTotal: 0,
    surfSessionsTotal: 0,
    surfSessionMinutesTotal: 0,
    wipeoutsTotal: 0,
    injuriesTotal: 0,
    damageTotal: 0,
    respectTotal: 0,
    gameDaysTotal: 0,
    criticalMovesTotal: 0
  }
}

export function createEmptyLocationStats(): LocationStats {
  const emptyLocationStats: any = {}
  for (const key in LOCATIONS) {
    emptyLocationStats[key] = createEmptyStats()
  }
  return emptyLocationStats as LocationStats
}

export function createEmptyAutomations(): Automations {
  const automations: Partial<Automations> = {}
  const automationTypes: AutomationEnum[] = [
    'rest',
    'work',
    'surf',
    'level_up',
    'location_change',
    'money_save',
    'job_change',
    'purchase',
    'stat_reach',
    'quest_complete',
    'contest_reach'
  ]

  automationTypes.forEach((type) => {
    automations[type] = {
      isUnlocked: type === 'rest',
      isActive: type === 'rest'
    }
  })

  return automations as Automations
}

export function createEmptyStokeState(): CurrencyState {
  return {
    amount: 0,
    max: BASE_STOKE_CAP,
    totalMultiplier: 1,
    unlockedLevels: INITIAL_UNLOCKED_SPEEDS
  }
}

export function createEmptyGameData(): GamePlayData {
  return {
    username:
      uniqueNamesGenerator({
        dictionaries: [colors, adjectives, animals],
        style: 'lowerCase'
      }) +
      '_' +
      Math.floor(Math.random() * 10000),
    tick: 0,
    gameMinutes: 0,
    currentLocation: 'mediterranean',
    currentLocationSpot: 'Marina di Pietrasanta',
    currencies: createEmptyCurrencies(),
    upgrades: createEmptyUpgradeStates(),
    skills: createEmptySkills(),
    stats: createEmptyStats(),
    locationStats: createEmptyLocationStats(),
    competitionStats: {},
    surfSession: createEmptySurfSession(),
    jobs: {},
    currentJob: undefined,
    currentActivity: 'rest',
    completedQuests: [],
    completedChapters: [],
    activeMainQuest: 'the_surf_lesson',
    timeline: [],
    isSurfingGreenWave: false,
    lastSaveTime: new Date().toISOString(),
    automations: createEmptyAutomations(),
    equipment: createEmptyEquipment(),
    tabs: {
      skills: 'skills',
      upgrades: 'shop',
      stats: 'global',
      jobs: 'local_labor'
    },
    currentCompetition: undefined,
    catchStoke: true
  }
}

function createUpgradeArray(): Upgrade[] {
  const upgradeArray = []
  for (const key in UPGRADES) {
    upgradeArray.push(UPGRADES[key as keyof Upgrades])
  }
  return upgradeArray
}
export const upgradeArray = createUpgradeArray()

function createCurrencyArray(): Currency[] {
  const currencyArray = []
  for (const key in CURRENCIES) {
    currencyArray.push(CURRENCIES[key as keyof Currencies])
  }
  return currencyArray
}
export const currencyArray = createCurrencyArray()

export function createEmptySurfSession(): SurfSessionData {
  return {
    isActive: false,
    computedSessionData: [],
    appliedSessionData: [],
    totalTime: 0,
    elapsedTime: 0,
    sessionInputs: null,
    critProbabilityComboMultiplier: 1
  }
}

export function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount)
  if (absAmount < 1000) {
    // For numbers less than 1000, show up to 2 decimal places
    const decimalPlaces = absAmount % 1 === 0 ? 0 : absAmount < 10 ? 2 : 1
    return absAmount.toFixed(decimalPlaces)
  }

  const suffixes = ['', 'k', 'M', 'B']
  const tier = Math.floor(Math.log10(absAmount) / 3)
  const suffix = suffixes[tier]
  const scaled = absAmount / Math.pow(1000, tier)

  // For scaled numbers, show 1 decimal if not whole number
  const decimalPlaces = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2
  return scaled.toFixed(decimalPlaces) + suffix
}

export function formatUnderscoreCase(text: string): string {
  if (!text) return ''
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function formatDuration(minutes: number, hideMinutes = false): string {
  const years = Math.floor(minutes / (365 * 24 * 60))
  const months = Math.floor((minutes % (365 * 24 * 60)) / (30 * 24 * 60))
  const days = Math.floor((minutes % (30 * 24 * 60)) / (24 * 60))
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const remainingMinutes = Math.floor(minutes % 60)

  let result = ''
  if (years > 0) result += `${years}y `
  if (months > 0) result += `${months}m `
  if (days > 0) result += `${days}d `
  if (hours > 0) result += `${hours}h `
  if (!hideMinutes && remainingMinutes > 0) result += `${remainingMinutes}m`

  return result.trim() || '0m'
}

export const getOverallSkillLevel = (
  skillLevels: SkillTreeLevel[],
  skillStates: SkillStates
): SkillTreeLevel | undefined => {
  for (let i = skillLevels.length - 1; i >= 0; i--) {
    const isUnlocked = Object.entries(skillLevels[i].unlockRequirements).every(
      ([skillKey, requiredLevel]) => skillStates[skillKey as SkillEnum].level >= (requiredLevel as number)
    )
    if (isUnlocked) {
      return skillLevels[i]
    }
  }
  return undefined
}

export function calculateOverallSkillProgress(
  unlockRequirements: SkillUnlockRequirements,
  gameSkills: SkillStates
): number {
  let totalCollectedPoints = 0
  let totalRequiredPoints = 0

  Object.entries(unlockRequirements).forEach(([skillKey, requiredLevel]) => {
    const skill = SKILLS[skillKey as SkillEnum]
    const requiredPoints = skill.levels[`${skillKey}${requiredLevel}` as keyof typeof skill.levels].points
    const collectedPoints = Math.min(gameSkills[skillKey as SkillEnum].points, requiredPoints)

    totalCollectedPoints += collectedPoints
    totalRequiredPoints += requiredPoints
  })

  return (totalCollectedPoints / totalRequiredPoints) * 100
}

export function getSurferRating(gameData: GamePlayData): number {
  const skillLevel = getOverallSkillLevel(SKILL_TREE, gameData.skills)
  if (!skillLevel) return 0
  const nextLevelIndex = skillLevel.level <= 10 ? skillLevel.level + 1 : 10
  const nextLevel = SKILL_TREE[nextLevelIndex]
  let progressPercent = Math.floor(calculateOverallSkillProgress(nextLevel.unlockRequirements, gameData.skills) / 10)
  if (progressPercent >= 100) progressPercent = 99
  return skillLevel.level + progressPercent / 10
}

export const isContestRuleMet = (requiredResult: SingleCompetitionStatEnum, competitionStats: any): boolean => {
  if (!competitionStats) return false

  // Define the order of results from highest to lowest achievement
  const resultOrder: SingleCompetitionStatEnum[] = [
    'won',
    'finals',
    'semifinals',
    'quarterfinals',
    'round_4',
    'round_3',
    'round_2',
    'round_1',
    'enrolled'
  ]

  // Find the index of the required result
  const requiredIndex = resultOrder.indexOf(requiredResult)
  if (requiredIndex === -1) return false

  // Check if the player has achieved this result or better
  for (let i = 0; i <= requiredIndex; i++) {
    const result = resultOrder[i]
    if (competitionStats[result] && competitionStats[result] > 0) {
      return true
    }
  }

  return false
}

export const isUnlockRuleMet = (rule: UnlockRule, gameData: GamePlayData, location?: LocationEnum): boolean => {
  switch (rule.category) {
    case 'level':
      if (rule.key === 'skill') {
        const lastAchievedLevel = getOverallSkillLevel(SKILL_TREE, gameData.skills)
        return lastAchievedLevel ? lastAchievedLevel.level >= (rule.value as number) : false
      } else {
        return gameData.skills[rule.key as keyof SkillStates]?.level >= (rule.value as number)
      }
    case 'currency':
      return gameData.currencies[rule.key as keyof CurrencyStates]?.amount >= (rule.value as number)
    case 'job':
      return (gameData.jobs[rule.key as JobEnum] || 0) >= (rule.value as number)
    case 'location':
      return gameData.currentLocation === rule.value
    case 'purchase':
      return gameData.upgrades[rule.key as keyof UpgradeStates]?.isPurchased ?? false
    case 'local_stat':
      return (
        gameData.locationStats[location || gameData.currentLocation][rule.key as keyof Stats] >= (rule.value as number)
      )
    case 'global_stat':
      return gameData.stats[rule.key as keyof Stats] >= (rule.value as number)
    case 'contest':
      return isContestRuleMet(
        rule.value as SingleCompetitionStatEnum,
        gameData.competitionStats[rule.key as CompetitionEnum]
      )
    default:
      return false
  }
}

export const formatMultiplier = (value: number) => {
  return `${value > 1 ? '+' : ''}${((value - 1) * 100).toFixed(0)}%`
}

export const checkUnlockRules = (requirements: UnlockRule[], gameData: GamePlayData): boolean => {
  return requirements.every((rule) => isUnlockRuleMet(rule, gameData))
}

export function calculateSkillLevel(skillKey: SkillEnum, points: number): number {
  const skillLevels = SKILLS[skillKey].levels
  const level = Object.keys(skillLevels)
    .map((key) => parseInt(key.replace(/[^0-9]/g, ''))) // Extract number from key (e.g., 'green_wave_riding3' -> 3)
    .reverse() // Start from highest level
    .find((levelNum) => points >= skillLevels[`${skillKey}${levelNum}`].points)

  return level ?? 0
}

export function getRandomWeightedValue(obj: { [key: string]: number }): string {
  const rand = Math.random()
  let sum = 0
  let highestProbKey = ''
  let highestProb = -1

  for (const key in obj) {
    sum += obj[key]
    if (rand <= sum) {
      return key
    }
    // Keep track of the highest probability
    if (obj[key] > highestProb) {
      highestProb = obj[key]
      highestProbKey = key
    }
  }
  return highestProbKey
}

export function getAdjustedSkillLevel(level: number): number {
  return Math.pow(level, 2) / Math.pow(5, 2)
}
export function getRandomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

export function isProductionBuild(): boolean {
  return import.meta.env.MODE === 'production'
}

export function getBrowserInfo() {
  return {
    isMobile: /Mobile|iP(hone|od|ad)|Android/i.test(navigator.userAgent),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    currentUrl: window.location.href,
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor
  }
}

export function getEnergyFitnessMultiplier(fitnessLevel: number): number {
  if (fitnessLevel <= 1) return 1
  if (fitnessLevel >= SKILL_LEVEL_CAP) return 3
  return [1, 1.25, 1.65, 2.25, 3][fitnessLevel - 1]
}

export function stateMigrations(gameData: GamePlayData): GamePlayData {
  let migratedData = { ...gameData }

  if (migratedData.completedChapters?.includes('portugal')) {
    migratedData.automations.surf = {
      ...migratedData.automations.surf,
      isUnlocked: true
    }
  }

  // Migration: Add automations if they don't exist
  if (!migratedData.automations || !migratedData.automations.contest_reach) {
    migratedData.automations = createEmptyAutomations()
    // Migration: Unlock work automation if Mediterranean chapter is completed
    if (migratedData.completedChapters?.includes('mediterranean')) {
      migratedData.automations.work = {
        ...migratedData.automations.work,
        isUnlocked: true
      }
    }
  }

  if (!migratedData.equipment) {
    migratedData.equipment = createEmptyEquipment()
  }

  if (!migratedData.tabs) {
    migratedData.tabs = {
      skills: 'skills',
      upgrades: 'shop',
      stats: 'global',
      jobs: 'local_labor'
    }
  }

  if (!migratedData.competitionStats) {
    migratedData.competitionStats = {}
  }

  if (!migratedData.activeMainQuest) {
    migratedData.activeMainQuest = findNextMainQuest(migratedData)
  }

  // Migration: Add stoke if it doesn't exist
  if (!migratedData.currencies.stoke || !migratedData.currencies.stoke.unlockedLevels) {
    migratedData.currencies.stoke = createEmptyStokeState()
  }

  if (!migratedData.gameMinutes) {
    migratedData.gameMinutes = getGameDurationMinutesOLD(migratedData.tick, false)
  }

  if (!migratedData.surfSession.critProbabilityComboMultiplier) {
    migratedData.surfSession.critProbabilityComboMultiplier = 1
  }

  // Migration: Add settings if they don't exist
  if (typeof migratedData.catchStoke === 'undefined') {
    migratedData.catchStoke = true
  }
  const skillLevel = getOverallSkillLevel(SKILL_TREE, migratedData.skills)?.level || 0
  migratedData.currencies.skill.totalMultiplier = getOverallSkillMultiplier(skillLevel)

  return migratedData
}

export function findNextMainQuest(gameData: GamePlayData): QuestEnum | undefined {
  const CHAPTERS = getAvailableChapters()

  // If no quests completed, return first quest of first chapter
  if (gameData.completedQuests.length === 0) {
    return CHAPTERS[0].quests[0] as QuestEnum
  }

  // Find the last completed main quest
  const lastCompletedMainQuest = gameData.completedQuests
    .map((questKey) => QUESTS[questKey])
    .filter((quest) => quest.type === 'main')
    .sort((a, b) => {
      // Sort by chapter first, then by location index within chapter
      if (a.chapterIndex !== b.chapterIndex) {
        return b.chapterIndex - a.chapterIndex
      }
      return b.locationIndex - a.locationIndex
    })[0]

  if (!lastCompletedMainQuest) {
    return CHAPTERS[0].quests[0] as QuestEnum
  }

  const currentChapter = CHAPTERS[lastCompletedMainQuest.chapterIndex - 1]
  if (!currentChapter) return undefined

  // Find index of last completed quest in current chapter
  const questIndex = currentChapter.quests.indexOf(lastCompletedMainQuest.key)

  // If there are more quests in current chapter
  if (questIndex < currentChapter.quests.length - 1) {
    return currentChapter.quests[questIndex + 1] as QuestEnum
  }

  // If this was the last quest in the chapter, try to move to next chapter
  const nextChapterIndex = lastCompletedMainQuest.chapterIndex
  const nextChapter = CHAPTERS[nextChapterIndex]

  // If there is no next chapter or we've reached the end of available chapters
  if (!nextChapter) {
    return undefined
  }

  // Return first quest of next chapter
  return nextChapter.quests[0] as QuestEnum
}

export function getGameDurationMinutesOLD(tick: number, isRealTime: boolean): number {
  if (isRealTime) {
    return (tick * TICK_INTERVAL_MILLISECONDS) / (1000 * 60)
  } else {
    return tick * BASE_GAME_MINUTES_PER_TICK
  }
}

export function getRealDurationMinutes(tick: number): number {
  return (tick * TICK_INTERVAL_MILLISECONDS) / (1000 * 60)
}

// Calculate offline stoke earned
export const calculateOfflineStoke = (
  lastSaveTime: string,
  currentTime: string,
  stokeMultiplier: number,
  stokeMax: number
): number => {
  try {
    // Convert string times to Date objects
    const lastSaveDate = new Date(lastSaveTime)
    const currentDate = new Date(currentTime)

    // Calculate time difference in hours
    const diffMs = currentDate.getTime() - lastSaveDate.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)

    // Calculate stoke earned (base rate * multiplier * hours)
    const stokeEarned = BASE_OFFLINE_STOKE_GAIN_PER_HOUR * stokeMultiplier * diffHours

    // Cap at max stoke capacity
    return Math.min(stokeEarned, stokeMax)
  } catch (error) {
    console.error('Error calculating offline stoke:', error)
    return 0
  }
}

// Get stoke consumption rate based on game speed
export function getStokeConsumptionRate(gameSpeed: GameSpeedTier): number {
  return STOKE_GAME_SPEED_COSTS[gameSpeed] || 0
}

// Helper to check if a career path is unlocked
export const isCareerPathUnlocked = (careerPath: CareerPath, gameData: GamePlayData): boolean => {
  return careerPath.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData))
}

// Helper to check if the game is running in an iframe
export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (e) {
    // If we can't access window.top, we're definitely in an iframe
    return true
  }
}

// Helper to check if a game save is from the demo version
export function isDemoGameSave(gameData: GamePlayData): boolean {
  // Check if cutback_back skill level is below 1
  const isCutbackSkillLow = gameData.skills.cutback_back?.level < 1

  // Check if current main quest is from chapter 2 or lower
  let isEarlyChapter = true
  if (gameData.activeMainQuest) {
    const activeQuest = QUESTS[gameData.activeMainQuest]
    isEarlyChapter = activeQuest?.chapterIndex <= 2
  }

  // Check if completed chapters doesn't include any advanced chapters
  // Demo only includes tutorial and mediterranean
  const hasAdvancedChapters = gameData.completedChapters.some(
    (chapter) => chapter !== 'mediterranean' && chapter !== 'france'
  )

  return isCutbackSkillLow && isEarlyChapter && !hasAdvancedChapters
}

// Helper to get count of unlocked jobs that haven't been worked in a career path
export const getUnworkedJobsCount = (careerPath: CareerPath, gameData: GamePlayData): number => {
  return careerPath.jobs.filter((jobKey) => {
    const job = JOBS[jobKey as JobEnum]
    // Check if job is unlocked but has not been worked
    return (
      job.unlockRules.every((rule) => isUnlockRuleMet(rule, gameData)) &&
      (gameData.jobs[jobKey as JobEnum] === undefined || gameData.jobs[jobKey as JobEnum] === 0)
    )
  }).length
}

// Helper to get total unlocked jobs across all career paths that haven't been worked
export const getTotalUnworkedJobsCount = (gameData: GamePlayData): number => {
  return Object.values(CAREERS)
    .filter((careerPath) => isCareerPathUnlocked(careerPath, gameData))
    .reduce((total, careerPath) => total + getUnworkedJobsCount(careerPath, gameData), 0)
}

// Helper to get count of available but unpurchased upgrades
export const getUnpurchasedUpgradesCount = (gameData: GamePlayData): number => {
  return Object.values(UPGRADES).filter((upgrade) => {
    const isPurchased = gameData.upgrades[upgrade.key]?.isPurchased
    const isVisible = upgrade.visibilityRules.every((rule) => isUnlockRuleMet(rule, gameData))
    const hasEnoughMoney = gameData.currencies.money.amount >= upgrade.cost

    // Count only visible, affordable, but not purchased upgrades
    return isVisible && !isPurchased && hasEnoughMoney
  }).length
}

// Helper to count quests that can be completed
export const getCompletableQuestsCount = (gameData: GamePlayData): number => {
  let completableMainQuest = false
  let completableSideQuest = false

  // Check for completable main quest (current active main quest)
  if (gameData.activeMainQuest) {
    const mainQuest = QUESTS[gameData.activeMainQuest]
    if (
      !gameData.completedQuests.includes(mainQuest.key) &&
      mainQuest.location === gameData.currentLocation &&
      checkUnlockRules(mainQuest.unlockRequirements || [], gameData)
    ) {
      completableMainQuest = true
    }
  }

  // Find first completable side quest
  const sideQuests = Object.values(QUESTS)
    .filter((quest) => quest.type === 'side')
    .filter((quest) => !gameData.completedQuests.includes(quest.key))
    .filter((quest) => quest.location === gameData.currentLocation)
    .sort((a, b) => a.locationIndex - b.locationIndex) // Sort by locationIndex

  // Check if the first uncompleted side quest is completable
  if (sideQuests.length > 0 && checkUnlockRules(sideQuests[0].unlockRequirements || [], gameData)) {
    completableSideQuest = true
  }

  // Return count of completable quests (maximum 2: one main and one side)
  return (completableMainQuest ? 1 : 0) + (completableSideQuest ? 1 : 0)
}

export function isDemoContentReached(gameData: GamePlayData): boolean {
  return gameData.completedQuests.includes('the_journey_continues')
}

// Helper to get energy consumption/recovery rate per hour based on current activity
export function getEnergyIncome(gameData: GamePlayData): number {
  const { currentActivity, currencies, currentJob } = gameData
  const energyMultiplier = currencies.energy.totalMultiplier || 1
  const isEnergyFull = currencies.energy.amount >= (currencies.energy.max || 0)

  switch (currentActivity) {
    case 'rest':
      // During rest, energy recovers at base rate * multiplier, unless energy is full
      return isEnergyFull ? 0 : BASE_ENERGY_REGENERATION_PER_HOUR * energyMultiplier

    case 'work':
      // During work, energy is consumed, but only if there's a current job
      if (!currentJob) return 0
      return -(60 / WORK_MINUTES_PER_ENERGY)

    case 'surf':
      // During surf, energy is consumed (60 minutes / minutes per energy)
      return -(60 / SURF_MINUTES_PER_ENERGY)

    default:
      return 0
  }
}

export function getOverallSkillMultiplier(skillLevel: number): number {
  return Math.max(1, Math.pow(skillLevel, 1.27) - 1)
}

// Get server time from Supabase
export async function getServerTime(): Promise<string> {
  try {
    const { data, error } = await supabase.rpc('get_server_time')
    if (error) throw error
    return data
  } catch (error) {
    console.error('Error getting server time:', error)
    return new Date().toISOString() // Fallback to client time
  }
}
